import { NavigationContainer } from '@react-navigation/native';
import '@/global.css';
import { GluestackUIProvider } from '@/components/ui/gluestack-ui-provider';
import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import * as Updates from 'expo-updates';
import i18n from 'i18n-js';
import React, {useEffect } from 'react';
import { Alert, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Provider, useSelector } from 'react-redux';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react-native';
import './assets/i18n';
import { GlobalContext } from './containers/GlobalState';
import Loader from './containers/Loader';
import { Notify } from './containers/Notify';
import store from './stores/store';
import { LogBox } from 'react-native';
import app from './app.json';
import { FcmNotifications } from "./containers/FcmNotifications";
import { StatusBar } from "expo-status-bar";
import { handleNotificationData, navigationRef } from "@/containers/navigation";


// LogBox.ignoreLogs(['If you do not provide children, you must specify an aria-label for accessibility']); // Ignore log notification by message
LogBox.ignoreAllLogs();//Ignore all log notifications


Sentry.init({
    dsn: 'https://ea27f5531047433082e7e64140d44118@sentry.ina.coach/2',
    enabled: !__DEV__,
    enabledNative: !__DEV__, // Set to true to enable Sentry for EAS builds.
    logLevel: 3,
    debug: true, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
    release: "de.bosconnect.inacoach.v3@" + app.expo.version,
    // dist: __DEV__ ? "42" : "99",
    // integrations: [
    //   new Sentry.Integrations.Breadcrumbs({
    //     console: false,
    //   }),
    // ]
});

const linking = {
    prefixes: [Linking.createURL('/'), 'inacoach://', 'https://test.app.ina.coach', 'https://app.ina.coach'],
    config: {
        screens: {
            // Studio dashboard
            Studio: {
                path: 'studio',
                screens: {
                    home: 'home',
                    contacts: 'contacts',
                    channels: 'channels',
                    community: 'community',
                }
            },

            // Studio-stack screens
            ViewContact: 'contact/:k',
            ViewChannel: 'channel/:k',
            editor: 'editor/:k?', // `k` is optional for now. See `Editor.js`

            // App
            MainStack: {
                initialRouteName: 'Home',
                screens: {
                    Workflows: 'workflows',
                    Planner: 'planner',
                    ModalMessage: {
                        path: 'notify/:screen/:p1/:p2'
                    },
                    ModalFcm: {
                        path: 'fcm/:type/:id'
                    },
                    ContactManagement: 'contactmanagement',
                },
            },
            contactManagement: 'contacts',
            appSettings: 'settings'
        }
    },
    async getInitialURL() {
        // First, you may want to do the default deep link handling
        // Check if app was opened from a deep link
        const url = await Linking.getInitialURL();
        console.ina('APP LINK', url)
        return url ?? undefined
    },
    subscribe(listener) {

        // externally received deep link
        const onReceiveURL = ({url}) => {
            console.ina('APP DEEP', url)
            listener(url)
        }
        // Listen to incoming links from deep linking
        const eventListenerSubscription = Linking.addEventListener('url', onReceiveURL);

        return () => eventListenerSubscription.remove()
    },
}

const devColors = {'staging-v2': 1, 'staging-v3': 4, 'default': Platform.OS === 'web' ? 0 : 3}

const Base = () => {
    const {currentlyRunning, isUpdateAvailable, isUpdatePending} = Updates.useUpdates();

    useEffect(() => {
        !__DEV__ && Alert.alert(
            i18n.t('global.info'),
            isUpdateAvailable ? i18n.t('global.updateAvailable') : i18n.t('global.noUpdateAvailable'),
            [{
                text: "OK", onPress: () => {
                    if (isUpdateAvailable) { // Update is available, so load the new update bundle
                        Updates.fetchUpdateAsync()
                    }
                },
            }]
        )
    }, [isUpdateAvailable]);

    useEffect(() => {
        if (isUpdatePending) { // Update has been successfully downloaded, so reload with the new update bundle delay by 2 sec in hope to avoid crash
            setTimeout(async () => {
                await Updates.reloadAsync()
            }, 2000)
        }
    }, [isUpdatePending]);

    const inAColor = useSelector((state) => state.settings.inAColor == undefined ? devColors[Updates.channel] || 0 : state.settings.inAColor)
    console.debug(linking);

    return (
        <GluestackUIProvider theme={'theme' + inAColor}>
            <NavigationContainer
                linking={linking}
                ref={navigationRef}
                onReady={async () => { // waits for the Navigation to build up
                    // check if the app was opened using a notification and then navigate accordingly
                    if (Platform.OS !== "web") {
                        const response = await Notifications.getLastNotificationResponseAsync();
                        const data = response.notification.request.content?.data?.nav ?? response.notification.request.trigger?.payload?.nav
                        if (data) {
                            handleNotificationData(data, 'tap');
                        }
                    }
                }}>
                <GestureHandlerRootView style={{flex: 1}}>
                    <FcmNotifications>
                        <GlobalContext inAColor={inAColor}>
                            <Loader/>
                        </GlobalContext>
                    </FcmNotifications>
                </GestureHandlerRootView>
            </NavigationContainer>
        </GluestackUIProvider>
    )
}

function App() {
    return (
        <>
            <StatusBar translucent /><Provider store={store}>
                <PersistGate loading={null} persistor={persistStore(store)}>
                    <Notify>
                        <Base/>
                    </Notify>
                </PersistGate>
            </Provider>
        </>
    );
}

export default Sentry.wrap(App);
